import React, { useState, useEffect, useCallback } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function NotificationSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({});
  const [originalSettings, setOriginalSettings] = useState(null);
  const TEST_BASE_URL = process.env.REACT_APP_BASE_URL;

  const fetchNotificationSettings = useCallback(async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("authToken");

      if (!token) {
        toast.error("Authentication required. Please log in.");
        setLoading(false);
        return;
      }

      const response = await fetch(`${TEST_BASE_URL}/notification-settings`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        toast.error(`Error fetching settings: ${response.status}`);
        setLoading(false);
        return;
      }

      const data = await response.json();
      const raw = data?.data || {};

      const normalized = {
        announcements: Boolean(raw.church_announcement),
        eventReminders: Boolean(raw.event_reminder),
        givingDonations: Boolean(raw.giving_donation),
        others: Boolean(raw.other),
        inApp: Boolean(raw.in_app),
        email: Boolean(raw.email),
        sms: Boolean(raw.sms),
        markAsRead: Boolean(raw.mark_as_read_when_opened),
        autoDelete: Boolean(raw.delete_after_30_days),
        confirmDelete: Boolean(
          raw.show_confirmation_before_deleting_notification
        ),
      };

      setSettings(normalized);
      setOriginalSettings(JSON.parse(JSON.stringify(normalized)));
      toast.success("Settings loaded successfully");
    } catch (error) {
      console.error("Error fetching notification settings:", error);
      toast.error("Failed to load notification settings.");
    } finally {
      setLoading(false);
    }
  }, [TEST_BASE_URL]);

  useEffect(() => {
    fetchNotificationSettings();
  }, [fetchNotificationSettings]);

  const handleCheckboxChange = (key) => {
    setSettings((prev) => {
      const updated = { ...prev, [key]: !prev[key] };
      if (key === "inApp") {
        localStorage.setItem("inAppNotifications", updated.inApp);
      }
      return updated;
    });
  };

  const hasChanges = () => {
    return JSON.stringify(settings) !== JSON.stringify(originalSettings);
  };

  const handleCancel = () => {
    if (originalSettings) {
      setSettings(JSON.parse(JSON.stringify(originalSettings)));
      toast.info("Changes discarded");
    }
  };

  const handleSave = async () => {
    if (!hasChanges()) {
      toast.info("No changes to save");
      return;
    }

    try {
      setSaving(true);

      const token = localStorage.getItem("authToken");

      if (!token) {
        toast.error("Authentication required. Please log in.");
        return;
      }

      const payload = {
        church_announcement: settings.announcements,
        event_reminder: settings.eventReminders,
        giving_donation: settings.givingDonations,
        other: settings.others,
        in_app: settings.inApp,
        email: settings.email,
        sms: settings.sms,
        mark_as_read_when_opened: settings.markAsRead,
        delete_after_30_days: settings.autoDelete,
        show_confirmation_before_deleting_notification: settings.confirmDelete,
      };

      const response = await fetch(`${TEST_BASE_URL}/notification-settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        toast.error(`Error saving settings: ${response.status}`);
        return;
      }

      await response.json();
      setOriginalSettings(JSON.parse(JSON.stringify(settings)));
      toast.success("Settings saved successfully");
    } catch (error) {
      console.error("Error saving notification settings:", error);
      toast.error("Failed to save notification settings.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-content">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-secondary">
            Loading notification settings...
          </p>
        </div>
        <ToastContainer position="top-right" autoClose={1500} />
      </div>
    );
  }

  return (
    <>
      <div className="settings-content">
        <h5>Notification Settings</h5>
        <p className="text-secondary">
          Choose how and when you want to stay updated on church activities,
          messages, and connections
        </p>
        <hr className="my-4" />

        <h6>What would you like to be notified about?</h6>
        <div className="notification-settings">
          {[
            { key: "announcements", label: "Church Announcements" },
            { key: "eventReminders", label: "Event Reminders" },
            { key: "givingDonations", label: "Giving & Donations" },
            { key: "others", label: "Others" },
          ].map(({ key, label }) => (
            <span key={key}>
              <div className="dotted">
                <div></div>
                {label}
              </div>
              <input
                type="checkbox"
                checked={Boolean(settings[key])}
                onChange={() => handleCheckboxChange(key)}
              />
            </span>
          ))}
        </div>

        <hr className="my-4" />
        <h6>Notification Channels (View Only)</h6>

        <div className="notification-settings">
          {[
            { key: "inApp", label: "In-App (When logged in)" },
            { key: "email", label: "Email" },
            { key: "sms", label: "SMS" },
          ].map(({ key, label }) => (
            <span key={key}>
              <div className="dotted">
                <div></div>
                {label}
              </div>
              <input
                type="checkbox"
                checked={Boolean(settings[key])}
                onChange={() => handleCheckboxChange(key)}
              />
            </span>
          ))}
        </div>

        <hr className="my-4" />
        <h6 className="pb-2">Notification Behaviour</h6>

        <div className="notification-settings">
          {[
            { key: "markAsRead", label: "Mark as read when opened" },
            {
              key: "autoDelete",
              label: "Auto delete after 30 days",
            },
          ].map(({ key, label }) => (
            <span key={key}>
              <div className="dotted">
                <div></div>
                {label}
              </div>
              <input
                type="checkbox"
                checked={Boolean(settings[key])}
                onChange={() => handleCheckboxChange(key)}
              />
            </span>
          ))}
        </div>

        <div className="profile-buttons">
          <button type="button" onClick={handleCancel} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!hasChanges() || saving}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={2000} />
    </>
  );
}

export default NotificationSettings;
